/**
 * theme.js — light/dark theme for HemaAI.
 *
 * Light is the default (clinical software convention, best for reading dense
 * lab values). Dark is opt-in and remembered per-device; it especially helps
 * the Smear / image views. Applied by toggling a `dark` class on <html> so
 * Tailwind's class-based dark: variants and the CSS in index.css take effect.
 * All storage access is guarded so a blocked localStorage never breaks the app.
 */
import { useCallback, useEffect, useState } from 'react';

const KEY = 'hemaai_theme';

export function getStoredTheme() {
  try { const t = localStorage.getItem(KEY); return t === 'dark' || t === 'light' ? t : null; }
  catch { return null; }
}

export function applyTheme(theme) {
  try {
    const el = document.documentElement;
    if (theme === 'dark') el.classList.add('dark');
    else el.classList.remove('dark');
  } catch { /* SSR / no document */ }
}

export function storeTheme(theme) {
  try { localStorage.setItem(KEY, theme); } catch { /* ignore */ }
}

/** Apply the saved theme (default light) as early as possible. */
export function initTheme() {
  const t = getStoredTheme() || 'light';
  applyTheme(t);
  return t;
}

/** React hook: current theme + toggle. */
export function useTheme() {
  const [theme, setTheme] = useState(() => getStoredTheme() || 'light');
  useEffect(() => { applyTheme(theme); }, [theme]);
  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      storeTheme(next);
      return next;
    });
  }, []);
  return { theme, toggle, isDark: theme === 'dark' };
}
